import { useMemo } from 'react';
import type { SessionsHeatmapCell } from '../../lib/api/analytics';

/**
 * 7×24 day-of-week × hour-of-day grid coloured by session intensity. All
 * times are Africa/Lagos (the API shifts UTC by +1h on the way out).
 *
 * Why this view: editorial cadence in news apps maps to *human* schedules,
 * not server clocks. Knowing that traffic spikes on weekday mornings 06:00–09:00
 * and again 19:00–22:00 directly informs (a) when to publish the daily
 * front-page story, (b) when to send breaking-news pushes for max
 * tap-through, and (c) when to schedule presenter-brief refreshes.
 */
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface SessionsHourHeatmapProps {
  cells: SessionsHeatmapCell[];
}

export function SessionsHourHeatmap({ cells }: SessionsHourHeatmapProps): JSX.Element {
  // Materialise into a dense 7×24 array so missing buckets show as zero
  // (which is meaningful — "nobody opens the app at 04:00 Mondays").
  const grid = useMemo(() => {
    const out: number[][] = Array.from({ length: 7 }, () =>
      new Array<number>(24).fill(0),
    );
    for (const c of cells) {
      if (c.dow >= 0 && c.dow < 7 && c.hour >= 0 && c.hour < 24) {
        out[c.dow]![c.hour] = c.uniques;
      }
    }
    return out;
  }, [cells]);

  const max = useMemo(
    () => grid.reduce((m, row) => Math.max(m, ...row), 0),
    [grid],
  );

  const peak = useMemo(() => {
    let bestDow = 0;
    let bestHour = 0;
    let bestVal = -1;
    for (let d = 0; d < 7; d += 1) {
      for (let h = 0; h < 24; h += 1) {
        const v = grid[d]![h]!;
        if (v > bestVal) {
          bestVal = v;
          bestDow = d;
          bestHour = h;
        }
      }
    }
    return { dow: bestDow, hour: bestHour, val: bestVal };
  }, [grid]);

  return (
    <div className="overflow-x-auto">
      <table className="border-separate" style={{ borderSpacing: 2 }}>
        <thead>
          <tr>
            <th className="w-10"></th>
            {Array.from({ length: 24 }, (_, h) => (
              <th
                key={h}
                className="text-[9.5px] font-mono font-medium uppercase tracking-wider text-ink-subtle"
                style={{ width: 22 }}
              >
                {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DAY_LABELS.map((label, dow) => (
            <tr key={dow}>
              <td className="pr-2 text-right text-[10.5px] font-mono font-medium uppercase tracking-wider text-ink-subtle">
                {label}
              </td>
              {Array.from({ length: 24 }, (_, h) => {
                const v = grid[dow]![h]!;
                const intensity = max > 0 ? v / max : 0;
                return (
                  <td
                    key={h}
                    title={`${label} ${String(h).padStart(2, '0')}:00 — ${v.toLocaleString()} unique device${v === 1 ? '' : 's'}`}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 3,
                      background: cellFill(intensity),
                      transition: 'transform 80ms ease',
                    }}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {peak.val > 0 ? (
        <p className="mt-3 text-xs text-ink-muted">
          <strong className="text-ink">Peak window:</strong> {DAY_LABELS[peak.dow]}{' '}
          {String(peak.hour).padStart(2, '0')}:00–{String((peak.hour + 1) % 24).padStart(2, '0')}:00 Lagos —{' '}
          <span className="font-mono">{peak.val.toLocaleString()}</span> unique
          devices.
        </p>
      ) : null}
    </div>
  );
}

/** Sequential brand-colour ramp with sqrt-easing so middle values stay
 *  visually distinguishable. Empty cells get a hairline neutral so the
 *  grid structure remains visible at zero. */
function cellFill(intensity: number): string {
  if (intensity <= 0) return 'rgba(228, 228, 231, 0.45)'; // zinc-200 @ 45%
  const eased = Math.sqrt(Math.max(0, Math.min(1, intensity)));
  // brand-red ramp; alpha 0.18..0.95.
  const alpha = 0.18 + eased * 0.77;
  return `rgba(220, 38, 38, ${alpha.toFixed(3)})`;
}
